// #522 — every class list shows the class NUMBER and sorts by it numerically.
// The class number (`classNumber`) is stamped from the FVD day sheet on
// reconcile; `className` is the class name.

// #1805 — two different constants, deliberately not one. scheduledTime is
// an FVD Access stamp, not a genuine UTC instant (#1811): FVD's Access DB
// holds a naive venue wall-clock time, ADODB on `fvdts`
// (America/Los_Angeles) parses that as box-local, and foxbox reads it back
// with UTC getters. Rendering an FVD stamp back through FVD_STAMP_TIMEZONE is
// the exact inverse of that mangling, which is the only reason these times
// have ever displayed correctly (the #612 "PST stopgap" comments below were
// load-bearing, not a leftover). A genuine server instant (e.g.
// updatedAt/`new Date().toISOString()`) has no such mangling to invert, so it
// belongs on the show's real venueTimezone instead (#2097: threaded in by
// formatVenueLocalDateTime's caller, and LOUD when the show has none, never
// the shared resolver's silent Pacific fallback). Do NOT swap either use for
// the other's constant — that is exactly the #1805 3-hour regression #1811
// headed off.
import { SUMMARY_DAYKEYS_VERSION } from "score-common/constants";
import { formatVenueDateTimeOrUnset, fvdStampDayKey, stampZoneOf } from "common-fe/venueTime";

// #1665 — was score-common/catalog's getCatalogTestById/getRideTestName,
// synchronous lookups over the build-time bundle. That bundle is retired
// (docs/score/catalog-audit-findings.md section 9); every caller here now
// passes an explicit `catalogDefsById` Map, built from the LIVE score-catalog
// table (GET /catalog, card #1662's useCatalogDefs hook / IndexedDB cache),
// keyed by def id. Returns null exactly where the bundle-backed version did:
// missing id, missing def, or (new failure mode, since the table is fetched
// async) the cache not yet hydrated.

/** Direct lookup by our own test def id against an explicit catalogDefsById
 * Map. */
export function getCatalogTestById(id, catalogDefsById) {
  if (id == null) return null;
  return catalogDefsById?.get(id) ?? null;
}

/** #915 — resolve the display name of the test a RIDE is riding, off
 * ride.testId against catalogDefsById (see getCatalogTestById above).
 * Resolution is always PER RIDE, never per class: Test-of-Choice classes let
 * different rides carry different testIds, so callers must pass the ride,
 * not the class. Returns null when the ride, its testId, or the catalog def
 * is missing/not yet hydrated. */
export function getRideTestName(ride, catalogDefsById) {
  return getCatalogTestById(ride?.testId, catalogDefsById)?.name ?? null;
}

// "12. Training Level Test 1" when a class number is present, else just the name.
export function classNumberLabel(c) {
  const n = c?.classNumber;
  const name = c?.className || c?.id || "";
  return n != null && `${n}`.trim() !== "" ? `${n}. ${name}` : name;
}

const leadingInt = (v) => {
  const m = /^\s*(\d+)/.exec(v == null ? "" : String(v));
  return m ? parseInt(m[1], 10) : NaN;
};

// Numeric-first comparator: classes with a numeric class number sort ascending
// by it (tie-break on the raw string, e.g. "12" before "12A"); classes with no
// number sort after them, keeping their incoming order (returns 0 → stable).
export function compareByClassNumber(a, b) {
  const na = leadingInt(a?.classNumber);
  const nb = leadingInt(b?.classNumber);
  const aOk = !Number.isNaN(na);
  const bOk = !Number.isNaN(nb);
  if (aOk && bOk) {
    return na - nb || String(a.classNumber).localeCompare(String(b.classNumber));
  }
  if (aOk) return -1;
  if (bOk) return 1;
  return 0;
}

// Stable numeric sort of a classes array (never mutates the input).
export function sortByClassNumber(classes) {
  return (classes ?? []).slice().sort(compareByClassNumber);
}

// #573/#591 — a class's scheduled start time as a short clock time. scheduledTime
// is an FVD-stamped string; render an ISO datetime in UTC (FVD stamps the naive
// local wall-clock with a "Z", so UTC shows the intended ring time regardless of
// the viewer's zone), and leave an already-formatted string ("8:30 AM") untouched.
//
// #1814 AC3 -- `scheduledTimeZone` is the optional per-row marker foxbox stamps
// when it composed the time in a configured venue zone (a genuine UTC instant);
// the stamp is then read in that zone. Pass the marker from the SAME row as `v`.
// Absent = the legacy Pacific-as-UTC stamp, read through FVD_STAMP_TIMEZONE.
export function formatClassStartTime(v, scheduledTimeZone) {
  if (!v) return null;
  const d = new Date(v);
  if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(v)) {
    // #612 / #1805 — render the FVD stamp back through FVD_STAMP_TIMEZONE
    // (the shared #1811 constant, was a local "America/Los_Angeles" literal).
    // Load-bearing, not a stopgap left over from before venueTimezone
    // existed: see the header comment above.
    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      timeZone: stampZoneOf(scheduledTimeZone),
    });
  }
  return String(v);
}

// #1012 — venue-local formatter for a genuine server timestamp (e.g. Publish
// Results' per-class federation-submission-state `updatedAt`, a real
// `new Date().toISOString()` instant). Unlike scheduledTime/classDayKey below
// (FVD's naive-local wall clock naively stamped with a "Z", which must be
// string-sliced, never Date-parsed), a server-stamped ISO timestamp IS a true
// UTC instant, so it belongs on the show's REAL venueTimezone (#1811 split),
// not the fixed FVD stamp zone formatClassStartTime uses above.
//
// #2097 — the show's zone is now a REQUIRED second argument (callers get it
// from hooks/useVenueDateTime, which reads the active show). A show with no
// usable zone renders `unsetText` (default: common-fe's "Venue timezone not
// set") instead of the Pacific time this used to print silently, and a
// configured zone prints its short label ("EDT") beside the time.
// `options` are Intl.DateTimeFormat options (default: medium date + short
// time). An unparseable value is shown verbatim, as before.
export function formatVenueLocalDateTime(v, venueTimezone, { unsetText, options } = {}) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return formatVenueDateTimeOrUnset(d, { timeZone: venueTimezone, unsetText, options });
}

// #3190 -- compact venue-local stamp for a work-segment time ("Oct 6, 9:05 AM").
// #2097 -- like formatVenueLocalDateTime, the show's zone is a required second
// argument (callers get a bound formatter from hooks/useVenueDateTime(
// VENUE_SHORT_OPTIONS)), and a show with no usable zone renders `unsetText`
// instead of a silent Pacific time.
export const VENUE_SHORT_OPTIONS = {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};
export function formatVenueLocalShort(v, venueTimezone, { unsetText } = {}) {
  return formatVenueLocalDateTime(v, venueTimezone, { unsetText, options: VENUE_SHORT_OPTIONS });
}

// #611 — real ingested classes carry no `day` field (reconcile never populates
// one); they only have `scheduledTime`. So day grouping found no day on any
// class and dumped everything under "No day". Derive a short weekday from
// scheduledTime, through FVD_STAMP_TIMEZONE (#1805/#1811 — an FVD stamp, not
// a stopgap), when `day` is absent, so classes group by day correctly.
// Prefer an explicit `day` if one is ever present.
export function classDay(c) {
  if (!c) return null;
  if (c.day) return c.day;
  const v = c.scheduledTime || c.time;
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  // #1814 AC3 -- the marker belongs to scheduledTime, never to the `time` fallback.
  const zone = c.scheduledTime ? c.scheduledTimeZone : undefined;
  return d.toLocaleDateString([], { weekday: "short", timeZone: stampZoneOf(zone) });
}

// #634 / #1823 — the day KEY a report is run for, and the `day` GET /ride is
// asked for (useShowRides/showRideDayPlan). It is the TRUE venue-local calendar
// day of scheduledTime, recovered through FVD_STAMP_TIMEZONE (common-fe's
// fvdStampDayKey, the #1811 twin of score-api's #1815 fvdStampDayKey).
//
// 🔴 This used to be `String(v).slice(0, 10)`, on the #590/#634 belief that an
// FVD stamp is naive-local with a trailing "Z". #1811 measured otherwise: the
// stamp is the venue wall clock mangled by fvdts' Pacific offset, so the raw
// date prefix rolls a late-afternoon/evening class to the NEXT day. #1815 fixed
// GET /ride?day= server-side to match the true day; a client still slicing
// would ask for a bucket no ride matches and silently drop that class's rides
// from RideEditor. It also now agrees with the day-scoped report defs
// (packages/report-defs/src/score/class-day.js, which read the stamp through
// Pacific since #689). classDay() above is the *display* grouping label.
export function classDayKey(c) {
  const v = c?.scheduledTime || c?.time;
  if (!v) return null;
  // #1814 AC3 -- read in the row's marker zone (absent = legacy stamp zone).
  return fvdStampDayKey(v, c.scheduledTime ? c.scheduledTimeZone : undefined) || null;
}

// #3327 -- the show summary's dayKeys, but ONLY when they were written by the
// true-venue-day rule (#1823). A summary row written before #1823 carries
// raw-sliced keys that roll an evening class onto the next day, and a
// finished/idle show's row is never rewritten, so trusting it would hide that
// evening's rides. Returns the keys (ascending, as stored) when the row is
// stamped dayKeysVersion >= SUMMARY_DAYKEYS_VERSION and non-empty, else null:
// callers fall back to deriving from the class list they already fetched
// (classDayKey), so the heal is on read with no re-reconcile and no extra read.
export function trustedSummaryDayKeys(summary) {
  if (!summary?.dayKeys?.length) return null;
  if (!(summary.dayKeysVersion >= SUMMARY_DAYKEYS_VERSION)) return null;
  return summary.dayKeys;
}

// #2855 — "today" in the SAME zone classDayKey reads a class's day through
// (#1823: FVD_STAMP_TIMEZONE), so the Placings "today's classes only" filter
// compares like with like: it must be the current wall-clock date at
// FVD_STAMP_TIMEZONE, never the viewer's own local date. An operator scoring
// from a different zone (or a box whose clock runs UTC) would otherwise see
// the filter disagree with the day keys for up to several hours a day. en-CA
// is the only built-in Intl locale whose default date format is exactly
// `YYYY-MM-DD`, matching classDayKey's output.
//
// #1814 AC3 -- a MARKED class's day key is its true venue-local calendar day,
// so its "today" is the current date in that same marker zone: pass the class's
// `scheduledTimeZone` as the second argument. Unmarked = the legacy stamp zone.
export function todayDayKey(now = new Date(), scheduledTimeZone) {
  return now.toLocaleDateString("en-CA", { timeZone: stampZoneOf(scheduledTimeZone) });
}

// "Fri, Jul 17" for a `YYYY-MM-DD` key — formatted at midday so no timezone can
// roll it to the adjacent day.
export function formatDayKey(key) {
  if (!key) return "";
  const d = new Date(`${key}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(key);
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
